/**
 * AuthorshipSettingsPanel — admin section for setting copyright compliance thresholds.
 * Embedded inside AdminHubPage as a collapsible section.
 *
 * Settings:
 *  - min_human_authorship_pct  (0–100, default 30)
 *  - warn_at_pct               (0–100, default 40)
 *  - enforcement_mode          warn_only | log_only
 *  - certificate_auto_generate boolean
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, ChevronDown, ChevronRight, Loader2, Save } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AuthorshipMeter } from '@/components/ui/AuthorshipMeter';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export function AuthorshipSettingsPanel() {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [settings, setSettings] = useState({
    min_human_authorship_pct:  30,
    warn_at_pct:               40,
    enforcement_mode:          'warn_only',
    certificate_auto_generate: true,
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/admin/authorship-settings`, { withCredentials: true })
      .then((r) => setSettings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/authorship-settings`, settings, { withCredentials: true });
      setSettings(r.data);
      toast.success('Authorship standards saved.');
    } catch {
      toast.error('Could not save authorship settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-8" data-testid="admin-authorship-settings-section">
      {/* Collapsible header */}
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left"
        onClick={() => setOpen((o) => !o)}
        data-testid="admin-authorship-settings-toggle"
      >
        <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
        <h2 className="text-[13px] font-semibold">Authorship Standards</h2>
        <span className="ml-1 text-[11px] text-muted-foreground font-normal">
          — copyright compliance thresholds &amp; certificate settings
        </span>
        {open
          ? <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
          : <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
        }
      </button>

      {open && (
        <div className="mt-3 rounded-md border border-border bg-background/60 p-4 space-y-5" data-testid="admin-authorship-settings-body">
          {loading ? (
            <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {/* Preview */}
              <div className="flex items-center gap-3 rounded-sm bg-panel-header/40 p-3 text-[12px]">
                <span className="text-muted-foreground">Preview:</span>
                <AuthorshipMeter pct={settings.warn_at_pct + 5} minPct={settings.min_human_authorship_pct} warnPct={settings.warn_at_pct} hasBaseline={true} />
                <AuthorshipMeter pct={settings.warn_at_pct - 5} minPct={settings.min_human_authorship_pct} warnPct={settings.warn_at_pct} hasBaseline={true} />
                <AuthorshipMeter pct={settings.min_human_authorship_pct - 5} minPct={settings.min_human_authorship_pct} warnPct={settings.warn_at_pct} hasBaseline={true} />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {/* Min threshold */}
                <div className="space-y-1.5">
                  <Label className="text-[11.5px]">
                    Block/warn threshold (%)
                    <span className="ml-1 text-muted-foreground font-normal text-[10px]">— chapters below this trigger a warning</span>
                  </Label>
                  <Input
                    type="number"
                    min={0} max={100}
                    value={settings.min_human_authorship_pct}
                    onChange={(e) => setSettings((s) => ({ ...s, min_human_authorship_pct: Number(e.target.value) }))}
                    data-testid="admin-authorship-min-pct"
                    className="w-full"
                  />
                </div>

                {/* Warn threshold */}
                <div className="space-y-1.5">
                  <Label className="text-[11.5px]">
                    Amber warning threshold (%)
                    <span className="ml-1 text-muted-foreground font-normal text-[10px]">— show amber badge between warn% and min%</span>
                  </Label>
                  <Input
                    type="number"
                    min={0} max={100}
                    value={settings.warn_at_pct}
                    onChange={(e) => setSettings((s) => ({ ...s, warn_at_pct: Number(e.target.value) }))}
                    data-testid="admin-authorship-warn-pct"
                    className="w-full"
                  />
                </div>
              </div>

              {/* Enforcement mode */}
              <div className="space-y-1.5">
                <Label className="text-[11.5px]">Enforcement at export</Label>
                <div className="flex gap-3">
                  {[
                    { value: 'warn_only',      label: 'Warn only',       desc: 'Author sees alert but can export anyway' },
                    { value: 'warn_and_block', label: 'Warn + block',    desc: 'Export locked until compliant' },
                    { value: 'log_only',       label: 'Log only',        desc: 'Silent — noted in audit log' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSettings((s) => ({ ...s, enforcement_mode: opt.value }))}
                      data-testid={`admin-authorship-enforcement-${opt.value}`}
                      className={cn(
                        'flex-1 rounded-md border p-2.5 text-left text-[11px] transition-colors',
                        settings.enforcement_mode === opt.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/50',
                      )}
                    >
                      <div className="font-semibold">{opt.label}</div>
                      <div className="mt-0.5 opacity-75">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Certificate toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="cert-auto"
                  checked={settings.certificate_auto_generate}
                  onChange={(e) => setSettings((s) => ({ ...s, certificate_auto_generate: e.target.checked }))}
                  data-testid="admin-authorship-cert-auto"
                  className="h-4 w-4 accent-primary"
                />
                <Label htmlFor="cert-auto" className="text-[11.5px] cursor-pointer">
                  Automatically include PDF certificate in every KDP export ZIP
                </Label>
              </div>

              <Button
                size="sm"
                onClick={save}
                disabled={saving}
                data-testid="admin-authorship-save-btn"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                Save standards
              </Button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
